import type { HelpTopic } from '../types';

export const tendersTopics: HelpTopic[] = [
  {
    id: 'wms.work-packages.create',
    title: 'Create a Work Package',
    icon: 'ri-stack-line',
    summary: 'Bundle one or more works so they can be tendered together.',
    sections: [
      {
        id: 'overview',
        title: 'Overview',
        body: (
          <p className="text-sm text-gray-700">
            A package groups similar works (same scheme / UDA) into a single tendering unit. Tenders are usually issued
            against packages, not individual works.
          </p>
        )
      },
      {
        id: 'steps',
        title: 'Steps',
        body: (
          <ol className="list-decimal list-inside space-y-1 text-sm text-gray-700">
            <li>Tenders page → <span className="font-medium">New Package</span>.</li>
            <li>Fill package name, scheme, FY.</li>
            <li>Select works — only works whose admin sanction is approved are eligible.</li>
            <li>Save; package total is the sum of the selected works' sanction amounts.</li>
          </ol>
        )
      }
    ],
    related: ['wms.tenders.create'],
    pageLinks: [
      { label: 'Open Tenders', to: '/app/ds-wms/tenders', icon: 'ri-file-paper-2-line' }
    ]
  },
  {
    id: 'wms.tenders.create',
    title: 'Create a Tender',
    icon: 'ri-file-paper-2-line',
    summary: 'Tender a package/work with the standard multi-step process.',
    sections: [
      {
        id: 'wizard',
        title: 'Wizard steps',
        body: (
          <ol className="list-decimal list-inside space-y-1 text-sm text-gray-700">
            <li><span className="font-medium">Tender Details</span> — scope, deadlines, EMD, doc fees.</li>
            <li><span className="font-medium">Bidders</span> — invite list; captured per bidder including submitted docs.</li>
            <li><span className="font-medium">Technical Evaluation</span> — shortlist.</li>
            <li><span className="font-medium">Financial Evaluation</span> — record quoted amounts.</li>
            <li><span className="font-medium">Award Decision</span> — confirm L1 or record direct award (see award topic).</li>
            <li><span className="font-medium">Post Award</span> — issue LOA, agreement, performance security.</li>
          </ol>
        )
      }
    ],
    related: ['wms.tenders.award'],
    pageLinks: [
      { label: 'Open Tenders', to: '/app/ds-wms/tenders', icon: 'ri-file-paper-2-line' }
    ]
  },
  {
    id: 'wms.tenders.award',
    title: 'Award a Tender',
    icon: 'ri-award-line',
    summary: 'Two paths: full competitive process, or direct award (nomination / single-tender).',
    sections: [
      {
        id: 'full-process',
        title: 'Full process (competitive)',
        body: (
          <ol className="list-decimal list-inside space-y-1 text-sm text-gray-700">
            <li>Complete Tech Evaluation — mark which bidders qualify.</li>
            <li>Enter Financial quotes — L1 is auto-computed.</li>
            <li>Open <span className="font-medium">Award Decision</span> → confirm awardee (default L1).</li>
            <li>Record award amount, LOA date, and agreement details.</li>
          </ol>
        )
      },
      {
        id: 'direct-award',
        title: 'Direct award',
        body: (
          <div className="space-y-2 text-sm text-gray-700">
            <p>
              Used for nomination, emergency, or single-tender cases. Skip Bidders/Tech/Financial steps in the wizard.
            </p>
            <ol className="list-decimal list-inside space-y-1">
              <li>On Tender Details choose award mode = <span className="font-medium">Direct Award</span>.</li>
              <li>Jump straight to Award Decision and pick the contractor (see contractor-assignment help).</li>
              <li>Record approval reference (GO no / resolution) in Remarks — this is the audit trail.</li>
            </ol>
          </div>
        )
      },
      {
        id: 'assign-contractor',
        title: 'Assign contractor',
        body: (
          <p className="text-sm text-gray-700">
            In the Award step, search the contractor registry (code / name / category). Pick one and the contact + bank
            details autofill. For first-time contractors, use <span className="font-medium">Create Contractor</span> from
            the Contractors page before coming back to this step.
          </p>
        )
      }
    ],
    related: ['wms.contractors.assign-in-award', 'wms.contractors.create'],
    pageLinks: [
      { label: 'Open Tenders', to: '/app/ds-wms/tenders', icon: 'ri-file-paper-2-line' },
      { label: 'Tender Execution', to: '/app/ds-wms/tender-execution', icon: 'ri-play-circle-line' }
    ]
  }
];
